import { memo, useState, SyntheticEvent, useCallback, useContext } from "react";
import { RoundIcon } from "./assets/round.icon";
import { Button } from "../../common/button/button";
import { getCourseStartDate } from "../../../utils/date.utils";
import { useLeadsForm } from "../../../form-hooks/use-leads-form";
import { StudentLead, LeadType } from "../../../model/leads";
import { Promocode } from "../../../model/promocode";
import { TelegramIcon } from "./assets/telegram.icon";
import { useRouter } from "next/router";
import { LottieChicken } from "../../../assets/animation/animated-chicken";
import { PhoneInput } from "react-international-phone";
import { AtLeastOneFrom } from "../../../form-hooks/use-validation";
import { CountryContext } from "../../../pages/_app";
import { t } from "../../../utils/locale.utils";
import "react-international-phone/style.css";
import { VALIDATIONS } from "../../../form-hooks/validations";
import { FormCmsData } from "../../../model/form";
import * as Sentry from "@sentry/nextjs";
import { getCourseStatus } from "../../../utils/course-utils";

interface Props {
  ranking: number;
  bootcampName: string;
  priceRubForEmail: string;
  priceRub: number;
  priceUsdt: string;
  promocodes: Promocode[];
  onApplyDiscount: (discount: number) => void;
  priceRubWithDiscount?: number;
  priceUsdtWithDiscount?: number;
  forms: FormCmsData;
  startTitle: string;
  orTitle: string;
  id: string;
}

export const Form = memo<Props>(
  ({
    ranking,
    bootcampName,
    priceRubForEmail,
    priceUsdt,
    promocodes,
    onApplyDiscount,
    priceRubWithDiscount,
    priceUsdtWithDiscount,
    forms,
    startTitle,
    orTitle,
    id,
  }) => {
    const { locale } = useRouter();

    const { isOptimism, isBlast, isFreeCourses } = getCourseStatus(id);

    const [inputValue, setInputValue] = useState("");
    const [appliedPromocode, setAppliedPromocode] = useState<Promocode>();
    const [isPromocodeValid, setIsPromocodeValid] = useState<boolean>();

    const handleChange = (event: SyntheticEvent<HTMLInputElement>) => {
      setInputValue(event.currentTarget.value);
    };

    const handlePromocodes = () => {
      const trimmedInput = inputValue.trim().toLowerCase();

      if (!trimmedInput) {
        alert("Please apply promocode");
        return;
      }
      const validPromocode = promocodes.find(
        (promocode) =>
          promocode.name.trim().toLowerCase() ===
          inputValue.toLowerCase().trim()
      );
      onInputChanged({
        name: "promocode",
        value: `${validPromocode?.name}, ${validPromocode?.discount}%`,
      });
      if (!validPromocode) {
        setIsPromocodeValid(false);
        return;
      }

      setIsPromocodeValid(true);
      setAppliedPromocode(validPromocode);
      onApplyDiscount(validPromocode.discount);
    };

    const {
      data,
      submit,
      isLoading,
      isSuccess,
      onInputChanged,
      validationErrors,
    } = useLeadsForm<StudentLead>(LeadType.STUDENT, {
      constraints: {
        requiredFields: [
          isFreeCourses
            ? "telegram"
            : new AtLeastOneFrom("telephone", "telegram"),
          "email",
          "name",
        ],
        customValidations: [
          VALIDATIONS.VALID_TELEPHONE,
          VALIDATIONS.VALID_TELEGRAM,
          VALIDATIONS.VALID_NAME,
          VALIDATIONS.VALID_EMAIL,
        ],
        isAddingToParagraphEnabled: true,
        isSendingToNotionEnabled: true,
      },
      params: {
        bootcampName: bootcampName,
        promocode: appliedPromocode,
        priceRub: isFreeCourses ? "0" : priceRubForEmail,
        priceUsdt: priceUsdt,
        priceRubWithDiscount: priceRubWithDiscount,
        priceUsdtWithDiscount: priceUsdtWithDiscount,
        locale: locale,
      },
      crmData: {
        formId: "Форма внутренней страницы для оплаты курса",
      },
      onError: (error) => {
        console.error("error", error);
        alert(forms.alertErrorMessage);
        const errorText = new Error(
          "Форма внутренней страницы для оплаты курса" + error
        );
        Sentry.captureException(errorText);
      },
    });
    const todayDate = new Date();

    const onPhoneInputChange = useCallback(
      (phone: string) => onInputChanged({ name: "telephone", value: phone }),
      [onInputChanged]
    );

    const country = useContext(CountryContext);

    return (
      <div
        className={
          isSuccess
            ? "flex justify-center h-auto lg:w-[33%] flex-col rounded-[20px] bg-white mt-5 lg:mt-0 p-10"
            : "flex h-auto lg:w-[33%] flex-col rounded-[20px] bg-white mt-5 lg:mt-0 p-10"
        }
      >
        <div className={isSuccess ? "" : "grow"}>
          <div
            className={
              isSuccess
                ? "hidden"
                : "flex w-fit items-center rounded-[30px] bg-day-base-base-100 pt-2 pb-2 pl-10 pr-10"
            }
          >
            <span className="mr-4">
              <RoundIcon />
            </span>
            <div>
              {isBlast ? "" : startTitle}{" "}
              {getCourseStartDate(
                isOptimism,
                isBlast,
                "Limited offer",
                todayDate,
                ranking,
                locale
              )}
            </div>
          </div>
          <div
            className={
              isSuccess
                ? "hidden"
                : "pt-10 text-[32px] leading-[112%] pb-20 lg:pb-0"
            }
            // eslint-disable-next-line
            dangerouslySetInnerHTML={{
              __html: forms.application.subtitle,
            }}
          />
        </div>
        <div className={isSuccess ? "text-center" : "hidden"}>
          <LottieChicken />
          <div
            // eslint-disable-next-line
            dangerouslySetInnerHTML={{
              __html: forms.successScreenTitle,
            }}
            className="text-[38px] pt-10 leading-[1.2]"
          />
          <div
            // eslint-disable-next-line
            dangerouslySetInnerHTML={{
              __html: forms.successScreenSubtitle,
            }}
            className="text-[16px] pt-6 max-w-[310px] m-auto leading-[1.2] text-day-base-base-400"
          />
        </div>
        <div role="form" className={isSuccess ? "hidden" : "orderForm"}>
          <div aria-label="Phone input light theme">
            <input
              className="lg:mt-6 w-full rounded-[12px] mb-7 bg-day-base-base-100 p-7 placeholder-day-base-base-400"
              placeholder={forms.placeholderName}
              name="name"
              value={data.name}
              onChange={onInputChanged}
            />
            {!isFreeCourses && (
              <PhoneInput
                country={country}
                value={data.telephone}
                onChange={onPhoneInputChange}
              />
            )}
          </div>
          {!isFreeCourses && (
            <div className="text-center lg:mt-5 pt-6 pb-6 lg:pt-0 lg:pb-0 text-night-base-base-400 text-[16px] pl-12 pr-12">
              {orTitle.toLocaleLowerCase()}
            </div>
          )}
          <input
            className="lg:mt-6 w-full rounded-[12px] bg-day-base-base-100 p-7 placeholder-day-base-base-400"
            placeholder={forms.placeholderTelegram}
            name="telegram"
            value={data.telegram}
            onChange={onInputChanged}
          />
          <input
            className="mt-6 w-full rounded-[12px] bg-day-base-base-100 p-7 placeholder-day-base-base-400"
            placeholder={forms.placeholderEmail}
            name="email"
            value={data.email}
            onChange={onInputChanged}
          />
          <div
            className={
              validationErrors.length ? "text-red text-[13px]" : "hidden"
            }
          >
            {validationErrors.length ? `${forms.fillTheFormMessage} ` : null}
            {validationErrors
              .map((error) => t(error.field.toString(), { or: orTitle }))
              .join(", ")}
          </div>
          <Button
            className="mt-6 btnSubmitForSeo"
            theme="primary-black"
            size="l"
            fluid
            disabled={isLoading}
            onClick={submit}
          >
            <span className="relative top-[-1px]">
              {isLoading ? forms.dataSendWaitMessage : forms.application.button}
            </span>
          </Button>
          <div
            className={
              isPromocodeValid === false
                ? "text-black text-center pt-10 text-[20px] leading-[1.2]"
                : "hidden"
            }
          >
            {forms.application.notValidPromocodeMessage}
          </div>

          <div
            className={
              isPromocodeValid && appliedPromocode
                ? "text-black text-center pt-10 text-[20px] leading-[1.2]"
                : "hidden"
            }
          >
            {forms.application.validPromocodeMessage}
          </div>
          {!isFreeCourses && (
            <div
              className={
                appliedPromocode || isPromocodeValid === false
                  ? "hidden"
                  : "flex"
              }
            >
              <input
                className="mt-6 mr-5 w-full rounded-[12px] bg-day-base-base-100 p-7 placeholder-day-base-base-400"
                placeholder={forms.application.promocodePlaceholder}
                name="promocode"
                onChange={handleChange}
              />

              <Button
                onClick={handlePromocodes}
                className="mt-6"
                theme="primary-black"
                size="l"
                fluid
              >
                <span className="relative top-[-1px]">
                  {forms.application.buttonToApplyPromocode}
                </span>
              </Button>
            </div>
          )}
          {!isFreeCourses && (
            <div className="lg:flex items-center pt-8 text-[16px] leading-[130%] lg:text-[16px] text-night-base-base-400">
              {forms.selfContact}&nbsp;
              <div className="text-blue flex transition-colors hover:text-opacity-80">
                <a
                  href="https://t.me/guidedao_eth"
                  target="_blank"
                  rel="noreferrer"
                >
                  {forms.selfContactLink}
                </a>
                <div className="pl-3 relative top-3">
                  <a
                    href="https://t.me/guidedao_eth"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <TelegramIcon />
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);
